import { ConsignmentDetails } from 'data/Consignment';
import { get, put, post } from 'lib/API';
import { PROCESSING } from 'constants/consignmentStates';
import { EDIT_APPROVAL_ACTIONS } from 'constants/order';

export const updateFulfillment = async (
  [consignmentId, updatedConsignment]: any[],
  { token }: any
): Promise<ConsignmentDetails> => {
  return await put(`v1/consignments/fulfillment/${consignmentId}`, token, updatedConsignment);
};

export const checkAndCaptureCredit = async (
  [creditId, consignmentId, fulfillmentData]: any[],
  { token }: any
): Promise<any> => {
  return await post(`v1/credit/capture/${creditId}/${consignmentId}`, token, fulfillmentData || {});
};

export const updateDelivery = async (
  [consignmentId, shippingConsignmentRequest, isConfirmed]: any,
  { token }: any
): Promise<ConsignmentDetails> => {
  if (isConfirmed === undefined) isConfirmed = true
  return await put(`v1/consignments/delivery/${consignmentId}`, token, { ...shippingConsignmentRequest, isConfirmed });
};

export const updateDispatch = async (
  [consignmentId, dispatchRequest]: any,
  { token }: any
): Promise<ConsignmentDetails> => {
  return await put(`v1/consignments/dispatch/${consignmentId}`, token, dispatchRequest);
};

export const loadConsignment = async (
  [consignmentId, vendorId]: any,
  { token }: any
): Promise<ConsignmentDetails> => {
  return await get(`v1/consignments/${consignmentId}${vendorId ? '?vendorId=' + vendorId : ''}`, token);
};

export const updateSalesOrderNumber = async (
  [consignmentId, salesOrderNumber]: any,
  { token }: any
): Promise<ConsignmentDetails> => {
  return await put(`v1/consignments/salesOrder/${consignmentId}`, token, { salesOrderNumber: salesOrderNumber });
};

export const updateState = async (
  [consignmentId, state, cancellationReason, cancelledBy, stockQuantity, reasonType, availableDate = null, fulfillmentData = null]: any,
  { token }: any
): Promise<ConsignmentDetails> => {
  let payload: any = {
    state,
    cancellationReason: cancellationReason || null,
    cancelledBy: cancelledBy || null,
    stockQuantity: stockQuantity || [],
    reasonType: reasonType || null
  };

  if (availableDate) {
    payload['availableDate'] = availableDate
  }

  if (fulfillmentData) {
    payload['fulfillmentData'] = fulfillmentData
  }

  return await put(`v1/consignments/${consignmentId}/state`, token, payload);
};

export const updateSubscriptionState = async (
  [consignmentId, state, subscriptionStartDate, subscriptionEndDate, subscriptionPeriod, paymentStatus]: any[],
  { token }: any
): Promise<ConsignmentDetails> => {
  const payload: any = { state, subscriptionStartDate, subscriptionEndDate, subscriptionPeriod };
  if (paymentStatus !== undefined) payload.paymentStatus = paymentStatus;
  return await put(`v1/consignments/${consignmentId}/state`, token, payload);
};

export const updateRentalState = async (
  [consignmentId, state, vendorAction = null, newRentalStartDate = null, newRentalEndDate = null, reason = null, paymentStatus, isOverridden = false, trackingId = null, estDate = null, carrier = null]: any,
  { token }: any
): Promise<ConsignmentDetails> => {
  return await put(`v1/consignments/updateRentalState/${consignmentId}`, token, {
    state,
    vendorAction,
    newRentalEndDate,
    newRentalStartDate,
    reason,
    paymentStatus,
    isOverridden,
    trackingId,
    estDate,
    carrier
  });
};

export const updateRentalStateV2 = async (
  [consignmentId, state, vendorAction = null, newRentalStartDate = null, newRentalEndDate = null, reason = null, consignmentEntries, onlyDatesChanged]: any,
  { token }: any
): Promise<ConsignmentDetails> => {
  return await put(`v1/consignments/updateRentalStateV2/${consignmentId}`, token, {
    state,
    vendorAction,
    newRentalEndDate,
    newRentalStartDate,
    reason,
    consignmentEntries,
    onlyDatesChanged
  });
};

export const saveConsignment = async ([consignment, dowJones]: any[], { token }: any): Promise<any> => {
  return await put(`v1/consignments`, token, {
    consignment,
    dowJones
  });
};

export const consignmentNewShortPayment = async (
  [consignmentId, paid, transactionNumber, date]: any,
  { token }: any
): Promise<any> => {
  return await post(`v1/consignments/shortpayments`, token, {
    consignmentId,
    paid,
    transactionNumber,
    date
  });
};

export const emulateSplitConsignment = async (
  [consignmentId, consignment]: any[],
  { token }: any
): Promise<any> => {

  const modifiedConsignment = {
    ...consignment,
    entries: consignment.entries.map((item: any) => {
      return {
        entryNumber: item.entryNumber,
        quantity: item.quantity,
        committedQuantity: item.committedQuantity,
        manufactureQuantity: item.manufactureQuantity,
        cancelQuantity: item.cancelQuantity,
        committedDate: item.committedDate || 0,
        manufactureDate: item.manufactureDate,
        committedEndDate: item.committedEndDate || 0,
        manufactureEndDate: item.manufactureEndDate || 0
      };
    })
  };

  return await post(`v1/consignments/splitConsignment/${consignmentId}`, token, modifiedConsignment);
};

export const updateAdditionalShippingCost = async (
  [consignmentId, additionalShippingCostRequest]: any,
  { token }: any
): Promise<ConsignmentDetails> => {
  return await put(`v1/consignments/additionalShippingCost/${consignmentId}`, token, additionalShippingCostRequest);
};


export const updateConsignmentEntrySerialNumber = async (
  [consignmentId, consignmentEntryId, serialNumber]: any,
  { token }: any
): Promise<ConsignmentDetails> => {
  const payload = {
    serialNumber,
    consignmentEntryId
  }
  return await put(`v1/consignments/serialNumber/${consignmentId}`, token, payload);
};

export const validateProductAddonMapping = async (
  [consignmentEntryId, sku, vendorId, consignmentId]: any,
  { token }: any
): Promise<ConsignmentDetails> => {
  const payload = {
    sku, consignmentEntryId, vendorId, consignmentId
  }
  return await post(`v1/personalServices/validateProductAddonMapping`, token, payload);
}


export const getConsignmentModifications = async (
  [modificationEntryId]: any,
  { token }: any
): Promise<ConsignmentDetails> => {
  return await get(`v1/consignments/modifications/${modificationEntryId}`, token);
};

export const reverseConsignment = async (
  [consignmentId]: any,
  { token }: any
): Promise<ConsignmentDetails> => {
  return await put(`v1/consignments/reverse/cancelation/${consignmentId}`, token, {});
};


export const updateSubscriptionModification = async (
  [consignmentId, newEndDate, reason]: any[],
  { token }: any
): Promise<void> => {
  return await put(`v1/consignments/updateSubscriptionModification/${consignmentId}`, token, { newEndDate, reason });
};

// Covers both subscription cancellation and affiliate deactivation via a shared endpoint
export const cancelConsignment = async (
  [consignmentId, reason]: any[],
  { token }: any
): Promise<void> => {
  return await put(`v1/consignments/cancel/${consignmentId}`, token, { reason });
};

export const applyOrderChanges = async (
  [consignmentId, items]: any[],
  { token }: any
): Promise<{ consignmentId: string; message: string }> => {
  return await put(`v1/consignments/applyOrderChanges/${consignmentId}`, token, items);
};

export const discardOrderEdits = async (
  [consignmentId]: any[],
  { token }: any
): Promise<{ consignmentId: string; message: string }> => {
  return await put(`v1/consignments/discardOrderEdits/${consignmentId}`, token, {});
};

export const withdrawEditApproval = async (
  [consignmentId]: any[],
  { token }: any
): Promise<{ consignmentId: string; message: string }> => {
  return await put(`v1/consignments/withdrawEditApproval/${consignmentId}`, token, {});
};

export const resendEditApproval = async (
  [consignmentId]: any[],
  { token }: any
): Promise<{ consignmentId: string; message: string }> => {
  return await put(`v1/consignments/resendEditApproval/${consignmentId}`, token, {});
};

// Approve-on-behalf rides on the generic state endpoint: the acceptance-date stamp and
// the snapshot close-out both live in that handler. The action marker is what lets the
// backend tell this apart from any other transition into PROCESSING.
export const approveEditOrderOnBehalf = async (
  [consignmentId]: any[],
  { token }: any
): Promise<ConsignmentDetails> => {
  return await put(`v1/consignments/${consignmentId}/state`, token, {
    state: PROCESSING,
    editApprovalAction: EDIT_APPROVAL_ACTIONS.APPROVE_ON_BEHALF
  });
};
